<template>
  <div class="text-center">
    <v-menu
      v-model="menu"
      :close-on-content-click="false"
      bottom
      offset-y
      @update:model-value="onMenuUpdate"
    >
      <template v-slot:activator="{ props }">
        <v-chip :color="isFiltered ? 'primary' : 'primary'" v-bind="props" label>
          {{ $t("common.filter.byProductGroup") }}
          <span v-if="isFiltered">&nbsp;|&nbsp;</span>
          <span v-if="isFiltered" class="font-weight-bold">
            {{ displaySelectedName }}
          </span>
          <v-btn
            v-if="isFiltered"
            variant="text"
            size="x-small"
            icon="mdi-close"
            @click.stop="reset"
          />
        </v-chip>
      </template>

      <v-card class="group-filter-card mt-2">
        <v-list>
          <v-list-item :title="$t('common.filter.byProductGroupTitle')">
            <template v-slot:append>
              <v-btn variant="text" icon="mdi-close" @click="menu = false" />
            </template>
          </v-list-item>
        </v-list>

        <v-divider />

        <div v-if="loading" class="d-flex justify-center align-center pa-4">
          <v-progress-circular indeterminate color="primary" />
          <span class="ml-2">{{ $t("common.action.loading") }}</span>
        </div>

        <div v-else class="pa-4">
          <v-text-field
            v-model="searchQuery"
            variant="outlined"
            :label="$t('common.search.productGroups')"
            prepend-inner-icon="mdi-magnify"
            density="compact"
            clearable
            class="mb-3"
          />

          <v-list density="compact" class="group-list pa-0">
            <v-list-item
              v-for="productClass in filteredProductClasses"
              :key="productClass.id"
              :value="productClass.id"
              :active="isItemSelected(productClass)"
              :title="productClass.name"
              class="product-class-item"
              @click="selectItem(productClass)"
            >
              <template v-slot:prepend>
                <v-avatar size="32" color="grey-lighten-3" :image="getDefaultImage(productClass)" />
              </template>

              <template v-slot:subtitle>
                <span v-if="shouldShowProductClassDescription(productClass)">
                  {{ productClass.description }}
                </span>
              </template>
            </v-list-item>

            <v-list-item v-if="filteredProductClasses.length === 0" class="text-center">
              <template v-slot:title>
                {{ $t("common.noResultsFound") }}
              </template>
            </v-list-item>
          </v-list>
        </div>

        <v-card-actions>
          <v-spacer />
          <v-btn
            color="primary"
            class="mr-2"
            :disabled="!selectedItem || loading"
            @click="applyFilter"
          >
            {{ $t("common.filter.apply") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-menu>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, watch } from "vue";
import { useProductsStore } from "@/catalog/stores/products";
import { useCatalogStore } from "@/catalog/stores/catalog";
import { useProductClassesStore } from "@/catalog/stores/productClasses";
import { useInventoryLogStore, InventoryLogFilter } from "@/catalog/stores/inventorylog";
import { CatalogProductClassItemWithDisplay } from "@/catalog/types/catalog";
import { useRoute } from "vue-router";

const props = defineProps({
  context: {
    type: String,
    default: "products",
    validator: (value: string) => ["products", "inventorylog"].includes(value),
  },
});

const menu = ref(false);
const selectedItem = ref<CatalogProductClassItemWithDisplay | null>(null);
const appliedItem = ref<CatalogProductClassItemWithDisplay | null>(null);
const loading = ref(false);
const searchQuery = ref("");
const route = useRoute();

const productsStore = useProductsStore();
const productClassesStore = useProductClassesStore();
const catalogStore = useCatalogStore();
const inventoryLogStore = useInventoryLogStore();

const productClasses = computed<CatalogProductClassItemWithDisplay[]>(() => {
  return catalogStore.getProductsWithPrices
    .filter(
      (item): item is CatalogProductClassItemWithDisplay =>
        item.type === "product_class" && item.active !== false,
    )
    .map((item) => ({
      ...item,
      displayName: item.name,
    }));
});

const filteredProductClasses = computed(() => {
  const query = (searchQuery.value ?? "").trim().toLowerCase();
  if (!query) {
    return productClasses.value;
  }

  return productClasses.value.filter(
    (productClass) =>
      productClass.name.toLowerCase().includes(query) ||
      (productClass.description && productClass.description.toLowerCase().includes(query)),
  );
});

const isFiltered = computed(() => !!appliedItem.value);

const displaySelectedName = computed(() => appliedItem.value?.name || "");

const shouldShowProductClassDescription = (
  productClass: CatalogProductClassItemWithDisplay,
): boolean => productClass.id === "__UNCATEGORIZED__" && !!productClass.description;

const isItemSelected = (item: CatalogProductClassItemWithDisplay) =>
  selectedItem.value?.id === item.id;

const selectItem = (item: CatalogProductClassItemWithDisplay) => {
  selectedItem.value = item;
};

const getDefaultImage = (item: CatalogProductClassItemWithDisplay): string => {
  const productClassImages = catalogStore.catalog[item.id]?.images;
  return productClassesStore.getProductClassDefaultImage(productClassImages);
};

const syncAppliedFromProductFilters = () => {
  const productClassId = productsStore.filterProductCondition.productClassId;
  appliedItem.value = productClassId
    ? productClasses.value.find((item) => item.id === productClassId) || null
    : null;
};

const syncAppliedFromInventoryLogFilters = () => {
  const productClassId = inventoryLogStore.filters.productClassId;
  appliedItem.value = productClassId
    ? productClasses.value.find((item) => item.id === productClassId) || null
    : null;
};

const syncAppliedFilter = () => {
  if (props.context === "products") {
    syncAppliedFromProductFilters();
  } else if (props.context === "inventorylog") {
    syncAppliedFromInventoryLogFilters();
  }
};

watch(
  () => productsStore.filterProductCondition,
  () => {
    if (props.context === "products") {
      syncAppliedFromProductFilters();
    }
  },
  { deep: true },
);

watch(
  () => inventoryLogStore.filters,
  () => {
    if (props.context === "inventorylog") {
      syncAppliedFromInventoryLogFilters();
    }
  },
  { deep: true },
);

const reset = () => {
  selectedItem.value = null;
  appliedItem.value = null;
  searchQuery.value = "";

  if (props.context === "products") {
    const filterProductCondition = { ...productsStore.filterProductCondition };
    delete filterProductCondition.productId;
    delete filterProductCondition.productClassId;
    productsStore.setFilterProductCondition(filterProductCondition);
    productsStore.searchProducts({}, false);
  } else if (props.context === "inventorylog") {
    const currentFilters = { ...inventoryLogStore.getFilters };
    const newFilters: InventoryLogFilter = {};

    for (const key in currentFilters) {
      if (key !== "productId" && key !== "productClassId" && key !== "priceId") {
        if (key === "action") newFilters.action = currentFilters.action;
        else if (key === "userId") newFilters.userId = currentFilters.userId;
        else if (key === "startDate") newFilters.startDate = currentFilters.startDate;
        else if (key === "endDate") newFilters.endDate = currentFilters.endDate;
        else if (key === "referenceId") newFilters.referenceId = currentFilters.referenceId;
        else if (key === "sku") newFilters.sku = currentFilters.sku;
      }
    }

    inventoryLogStore.setFilters(newFilters);
  }
};

const applyFilter = () => {
  if (!selectedItem.value) {
    menu.value = false;
    return;
  }

  appliedItem.value = selectedItem.value;

  if (props.context === "products") {
    const filterProductCondition = { ...productsStore.filterProductCondition };
    delete filterProductCondition.productId;
    filterProductCondition.productClassId = selectedItem.value.id;
    productsStore.setFilterProductCondition(filterProductCondition);
    productsStore.searchProducts({}, false);
  } else if (props.context === "inventorylog") {
    const currentFilters = { ...inventoryLogStore.getFilters };
    const newFilters: InventoryLogFilter = {};

    for (const key in currentFilters) {
      if (key !== "productId" && key !== "productClassId" && key !== "priceId") {
        if (key === "action") newFilters.action = currentFilters.action;
        else if (key === "userId") newFilters.userId = currentFilters.userId;
        else if (key === "startDate") newFilters.startDate = currentFilters.startDate;
        else if (key === "endDate") newFilters.endDate = currentFilters.endDate;
        else if (key === "referenceId") newFilters.referenceId = currentFilters.referenceId;
        else if (key === "sku") newFilters.sku = currentFilters.sku;
      }
    }

    newFilters.productClassId = selectedItem.value.id;
    inventoryLogStore.setFilters(newFilters);
  }

  menu.value = false;
};

const onMenuUpdate = (isOpen: boolean) => {
  if (!isOpen) {
    return;
  }

  selectedItem.value = appliedItem.value;
  searchQuery.value = "";
};

const getQueryParamString = (value: unknown): string | undefined => {
  if (Array.isArray(value)) {
    return typeof value[0] === "string" ? value[0] : undefined;
  }
  return typeof value === "string" ? value : undefined;
};

const getInventoryLogFiltersWithoutCatalogProduct = (): InventoryLogFilter => {
  const currentFilters = { ...inventoryLogStore.getFilters };
  const newFilters: InventoryLogFilter = {};

  for (const key in currentFilters) {
    if (key !== "productId" && key !== "productClassId" && key !== "priceId") {
      if (key === "action") newFilters.action = currentFilters.action;
      else if (key === "userId") newFilters.userId = currentFilters.userId;
      else if (key === "startDate") newFilters.startDate = currentFilters.startDate;
      else if (key === "endDate") newFilters.endDate = currentFilters.endDate;
      else if (key === "referenceId") newFilters.referenceId = currentFilters.referenceId;
      else if (key === "sku") newFilters.sku = currentFilters.sku;
    }
  }

  return newFilters;
};

const applyProductIdQueryParamFilter = (): boolean => {
  const productId = getQueryParamString(route.query.productId);
  if (props.context !== "inventorylog" || !productId) {
    return false;
  }

  const newFilters = getInventoryLogFiltersWithoutCatalogProduct();
  newFilters.productId = productId;
  inventoryLogStore.setFilters(newFilters);
  selectedItem.value = null;
  appliedItem.value = null;
  return true;
};

const applyProductClassQueryParamFilter = (): boolean => {
  const productClassId = getQueryParamString(route.query.productClassId);
  if (!productClassId) {
    return false;
  }

  const productClass = productClasses.value.find((item) => item.id === productClassId);
  if (!productClass) {
    return false;
  }

  selectedItem.value = productClass;
  appliedItem.value = productClass;
  applyFilter();
  return true;
};

const applyQueryParamFilters = () => {
  if (applyProductIdQueryParamFilter()) {
    return;
  }
  applyProductClassQueryParamFilter();
};

onMounted(async () => {
  try {
    loading.value = true;
    await catalogStore.fetchCatalog();

    if (route.query.productId || route.query.productClassId) {
      applyQueryParamFilters();
    } else {
      syncAppliedFilter();
      selectedItem.value = appliedItem.value;
    }
  } catch (error) {
    console.error("Failed to fetch catalog data:", error);
  } finally {
    loading.value = false;
  }
});

watch(
  () => [route.query.productId, route.query.productClassId],
  () => {
    if (productClasses.value.length > 0) {
      applyQueryParamFilters();
    }
  },
);
</script>

<style scoped>
.group-filter-card {
  width: min(520px, calc(100vw - 32px));
  max-width: calc(100vw - 32px);
}

.group-list {
  max-height: 350px;
  overflow-y: auto;
}

.group-list :deep(.v-list-item-title),
.group-list :deep(.v-list-item-subtitle) {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.product-class-item {
  font-weight: 500;
}

@media (max-width: 600px) {
  .group-filter-card {
    width: calc(100vw - 16px);
    max-width: calc(100vw - 16px);
  }
}
</style>
